import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import oldQuestions from '../jsons/oldquestions.json';
import ExplanationRenderer from './ExplanationRenderer';
import VisualMathInput from './VisualMathInput';
import MathInputHelp from './MathInputHelp';
import QuizResultsPage from './QuizResultsPage';
import { usePremium } from '../contexts/PremiumContext';
import { autoConvertMathNotation, shouldTriggerConversion, adjustCursorPosition } from '../utils/mathNotationUtils';

const OldQuestionPage = ({ onShowPayment }) => {
  const { isPremium: userHasPremium, getPremiumStatusInfo } = usePremium();
  
  // Debug logging for premium context testing
  console.log('OldQuestionPage - Premium Context Status:', {
    userHasPremium,
    getPremiumStatusInfo: getPremiumStatusInfo ? getPremiumStatusInfo() : 'function not available',
    timestamp: new Date().toISOString()
  });
  
  const [selectedSet, setSelectedSet] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [currentSubAnswers, setCurrentSubAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(70 * 60); // 70 minutes in seconds
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false); // New state for results view
  const [cursorPosition, setCursorPosition] = useState(0);
  const [activeSubInput, setActiveSubInput] = useState(null); // Track which sub-input is active
  const [subInputRefs] = useState({}); // Store refs for sub-inputs
  const [previousAnswer, setPreviousAnswer] = useState(''); // Track previous answer for conversion
  const [previousSubAnswers, setPreviousSubAnswers] = useState({}); // Track previous sub-answers
  const [showMathHelp, setShowMathHelp] = useState(false); // Control help modal
  
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  
  // Get available question sets
  const availableSets = Object.keys(oldQuestions).filter(key => key !== 'metadata');
  const currentQuestions = useMemo(() => 
    selectedSet ? oldQuestions[selectedSet] : [], 
    [selectedSet]
  );
  const currentQuestion = currentQuestions[currentQuestionIndex];
  
  // Helper function to determine if a question has sub-questions
  const hasSubQuestions = useCallback((question) => {
    return question && typeof question.question_text === 'object' && 
           question.question_text.sub_questions && 
           Array.isArray(question.question_text.sub_questions);
  }, []);
  
  // Helper function to get sub-questions for a question
  const getSubQuestions = useCallback((question) => {
    if (hasSubQuestions(question)) {
      return question.question_text.sub_questions;
    }
    return [];
  }, [hasSubQuestions]);
  
  // Helper function to get question text (main part)
  const getMainQuestionText = useCallback((question) => {
    if (!question) return '';
    if (typeof question.question_text === 'string') {
      return question.question_text;
    }
    return question.question_text.main || '';
  }, []);
  
  // Protection and payment helper functions
  const isSetProtected = useCallback((setName) => {
    return oldQuestions.metadata && 
           oldQuestions.metadata[setName] && 
           oldQuestions.metadata[setName].protected === true;
  }, []);
  
  const canUserAccessSet = useCallback((setName) => {
    return !isSetProtected(setName) || userHasPremium;
  }, [isSetProtected, userHasPremium]);
  
  const getSetMetadata = useCallback((setName) => {
    return oldQuestions.metadata && oldQuestions.metadata[setName] 
      ? oldQuestions.metadata[setName] 
      : { title: setName, protected: false, description: '', price: null };
  }, []);
  
  // Convert user-friendly input to LaTeX notation for validation
  const convertToLatex = (userInput) => {
    if (!userInput) return '';
    
    // Symbol mapping from display to LaTeX
    const symbolMap = {
      // Basic arithmetic
      '×': '\\times',
      '÷': '\\div',
      '±': '\\pm',
      
      // Greek letters
      'π': '\\pi',
      'θ': '\\theta',
      'α': '\\alpha',
      'β': '\\beta',
      'γ': '\\gamma',
      'Δ': '\\Delta',
      
      // Calculus symbols
      '∫': '\\int',
      '∂': '\\partial',
      '∞': '\\infty',
      
      // Comparison operators
      '≤': '\\leq',
      '≥': '\\geq',
      '≠': '\\neq',
      '≈': '\\approx',
      
      // Powers (handle special cases)
      '²': '^{2}',
      '³': '^{3}',
      
      // Square root
      '√': '\\sqrt',
    };
    
    let latexString = userInput;
    
    // Replace symbols
    Object.entries(symbolMap).forEach(([symbol, latex]) => {
      latexString = latexString.replace(new RegExp(symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), latex);
    });
    
    // Handle fraction patterns like "a/b" -> "\frac{a}{b}" (but avoid conflicting with templates)
    if (!latexString.includes('\\frac')) {
      latexString = latexString.replace(/([^/\s]+)\/([^/\s]+)/g, '\\frac{$1}{$2}');
    }
    
    // Handle power patterns like "x^n" or "x^{n}" (but avoid conflicting with templates)
    if (!latexString.includes('^{}')) {
      latexString = latexString.replace(/\^([a-zA-Z0-9])/g, '^{$1}');
    }
    
    // Handle function patterns
    latexString = latexString.replace(/sin⁻¹/g, '\\sin^{-1}');
    latexString = latexString.replace(/cos⁻¹/g, '\\cos^{-1}');
    latexString = latexString.replace(/tan⁻¹/g, '\\tan^{-1}');
    latexString = latexString.replace(/\bsin\b/g, '\\sin');
    latexString = latexString.replace(/\bcos\b/g, '\\cos');
    latexString = latexString.replace(/\btan\b/g, '\\tan');
    latexString = latexString.replace(/\bln\b/g, '\\ln');
    latexString = latexString.replace(/\blog\b/g, '\\log');
    latexString = latexString.replace(/\blim\b/g, '\\lim');
    
    // Handle integral bounds like "∫₀¹"
    latexString = latexString.replace(/∫₀¹/g, '\\int_{0}^{1}');
    latexString = latexString.replace(/∫([^_]+)_([^{]+)\^([^{]+)/g, '\\int_{$2}^{$3}');
    
    // Clean up extra spaces and normalize
    return latexString.replace(/\s+/g, ' ').trim();
  };
  
  // Normalize answers for comparison (remove whitespace, handle common variations)
  const normalizeAnswer = (answer) => {
    if (!answer) return '';
    return answer.toString()
      .replace(/\s+/g, '')
      .replace(/\$+/g, '')
      .toLowerCase()
      .trim();
  };
  
  // Calculate score based on answered questions
  const calculateScore = useCallback(() => {
    let correctCount = 0;
    let totalQuestions = 0;
    
    Object.entries(userAnswers).forEach(([questionIndex, answers]) => {
      const question = currentQuestions[parseInt(questionIndex)];
      if (question) {
        if (hasSubQuestions(question)) {
          // Handle sub-questions
          const subQuestions = getSubQuestions(question);
          totalQuestions += subQuestions.length;
          
          if (typeof answers === 'object') {
            subQuestions.forEach((subQ, index) => {
              const partKey = `part_${index + 1}`;
              const userAnswer = answers[partKey];
              const correctAnswer = typeof question.final_answer === 'object' 
                ? question.final_answer[partKey] 
                : null;
              
              if (userAnswer && correctAnswer) {
                const userLatex = convertToLatex(userAnswer);
                const correctLatex = correctAnswer;
                if (normalizeAnswer(userLatex) === normalizeAnswer(correctLatex)) {
                  correctCount++;
                }
              }
            });
          }
        } else {
          // Handle simple questions
          totalQuestions += 1;
          const userLatex = convertToLatex(answers);
          const correctLatex = question.final_answer;
          if (normalizeAnswer(userLatex) === normalizeAnswer(correctLatex)) {
            correctCount++;
          }
        }
      }
    });
    
    return { correctCount, totalQuestions };
  }, [userAnswers, currentQuestions, hasSubQuestions, getSubQuestions]);
  
  // Submit quiz
  const handleSubmitQuiz = useCallback(() => {
    if (hasSubQuestions(currentQuestion)) {
      // Save current sub-answers
      if (Object.keys(currentSubAnswers).length > 0) {
        setUserAnswers(prev => ({
          ...prev,
          [currentQuestionIndex]: { subAnswers: currentSubAnswers }
        }));
      }
    } else {
      // Save current simple answer
      if (currentAnswer.trim()) {
        setUserAnswers(prev => ({
          ...prev,
          [currentQuestionIndex]: { answer: currentAnswer.trim() }
        }));
      }
    }
    
    setQuizCompleted(true);
    setShowResults(true); // Show results instead of basic completion screen
    clearInterval(timerRef.current);
  }, [currentAnswer, currentSubAnswers, currentQuestionIndex, currentQuestion, hasSubQuestions]);
  
  // Timer effect
  useEffect(() => {
    if (!quizCompleted && timeRemaining > 0 && selectedSet) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmitQuiz();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    
    return () => clearInterval(timerRef.current);
  }, [quizCompleted, timeRemaining, handleSubmitQuiz, selectedSet]);
  
  // Format time display
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Handle answer input change
  const handleAnswerChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    setCurrentAnswer(newValue);
    setCursorPosition(cursorPos);
    
    // Apply auto-conversion if trigger conditions are met
    if (shouldTriggerConversion(newValue, previousAnswer)) {
      setTimeout(() => {
        const converted = autoConvertMathNotation(newValue);
        if (converted !== newValue) {
          const adjustedCursor = adjustCursorPosition(newValue, converted, cursorPos);
          setCurrentAnswer(converted);
          setCursorPosition(adjustedCursor);
          
          // Update input cursor position
          if (inputRef.current) {
            inputRef.current.setSelectionRange(adjustedCursor, adjustedCursor);
          }
        }
      }, 100); // Small delay to allow natural typing
    }
    
    setPreviousAnswer(newValue);
  };
  
  // Handle cursor position change
  const handleCursorChange = (e) => {
    setCursorPosition(e.target.selectionStart);
  };
  
  // Handle sub-question input changes
  const handleSubAnswerChange = (partKey, value, cursorPos) => {
    setCurrentSubAnswers(prev => ({
      ...prev,
      [partKey]: value
    }));
    setActiveSubInput(partKey);
    setCursorPosition(cursorPos);
    
    // Apply auto-conversion for sub-questions
    const previousValue = previousSubAnswers[partKey] || '';
    if (shouldTriggerConversion(value, previousValue)) {
      setTimeout(() => {
        const converted = autoConvertMathNotation(value);
        if (converted !== value) {
          const adjustedCursor = adjustCursorPosition(value, converted, cursorPos);
          setCurrentSubAnswers(prev => ({
            ...prev,
            [partKey]: converted
          }));
          setCursorPosition(adjustedCursor);
          
          // Update input cursor position
          const ref = subInputRefs[partKey];
          if (ref && ref.current) {
            ref.current.setSelectionRange(adjustedCursor, adjustedCursor);
          }
        }
      }, 100);
    }
    
    setPreviousSubAnswers(prev => ({
      ...prev,
      [partKey]: value
    }));
  };
  
  // Handle sub-question input focus
  const handleSubInputFocus = (partKey) => {
    setActiveSubInput(partKey);
  };
  
  // Handle main input focus
  const handleMainInputFocus = () => {
    setActiveSubInput(null);
  };
  
  // Save current answer
  const handleSaveAnswer = () => {
    if (hasSubQuestions(currentQuestion)) {
      if (Object.keys(currentSubAnswers).length > 0) {
        setUserAnswers(prev => ({
          ...prev,
          [currentQuestionIndex]: { subAnswers: currentSubAnswers }
        }));
        setCurrentSubAnswers({});
      }
    } else {
      if (currentAnswer.trim()) {
        setUserAnswers(prev => ({
          ...prev,
          [currentQuestionIndex]: { answer: currentAnswer.trim() }
        }));
        setCurrentAnswer('');
      }
    }
  };
  
  // Navigate to next question
  const handleNextQuestion = () => {
    if (hasSubQuestions(currentQuestion)) {
      if (Object.keys(currentSubAnswers).length > 0) {
        handleSaveAnswer();
      }
    } else {
      if (currentAnswer.trim()) {
        handleSaveAnswer();
      }
    }
    
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      loadAnswersForQuestion(currentQuestionIndex + 1);
    }
  };
  
  // Navigate to previous question
  const handlePreviousQuestion = () => {
    if (hasSubQuestions(currentQuestion)) {
      if (Object.keys(currentSubAnswers).length > 0) {
        handleSaveAnswer();
      }
    } else {
      if (currentAnswer.trim()) {
        handleSaveAnswer();
      }
    }
    
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      loadAnswersForQuestion(currentQuestionIndex - 1);
    }
  };
  
  // Skip current question
  const handleSkipQuestion = () => {
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      loadAnswersForQuestion(currentQuestionIndex + 1);
    }
  };
  
  // Load answers for a specific question
  const loadAnswersForQuestion = useCallback((questionIndex) => {
    const question = currentQuestions[questionIndex];
    const savedAnswers = userAnswers[questionIndex];
    
    if (hasSubQuestions(question)) {
      setCurrentSubAnswers(savedAnswers?.subAnswers || {});
      setCurrentAnswer('');
    } else {
      setCurrentAnswer(savedAnswers?.answer || '');
      setCurrentSubAnswers({});
    }
    
    // Reset active input state
    setActiveSubInput(null);
    setCursorPosition(0);
  }, [currentQuestions, userAnswers, hasSubQuestions]);
  
  // Load answer when question changes
  useEffect(() => {
    loadAnswersForQuestion(currentQuestionIndex);
  }, [currentQuestionIndex, loadAnswersForQuestion]);
  
  // Handle set selection
  const handleSetSelection = (setName) => {
    // Check if the set is protected and user doesn't have premium access
    if (isSetProtected(setName) && !userHasPremium) {
      if (onShowPayment) {
        onShowPayment();
      }
      return;
    }
    
    // User can access this set
    setSelectedSet(setName);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setCurrentAnswer('');
    setCurrentSubAnswers({});
    setTimeRemaining(70 * 60);
    setQuizCompleted(false);
    setActiveSubInput(null);
    setCursorPosition(0);
  };
  
  // Reset quiz
  const handleResetQuiz = () => {
    setSelectedSet(null);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setCurrentAnswer('');
    setCurrentSubAnswers({});
    setTimeRemaining(70 * 60);
    setQuizCompleted(false);
    setShowResults(false);
    setActiveSubInput(null);
    setCursorPosition(0);
  };

  // Handle back to quiz from results
  const handleBackToQuiz = () => {
    setShowResults(false);
    setQuizCompleted(false);
  };

  // Handle take new quiz from results  
  const handleTakeNewQuiz = () => {
    handleResetQuiz();
  };
  
  if (quizCompleted && showResults) {
    return (
      <QuizResultsPage
        userAnswers={userAnswers}
        questions={currentQuestions}
        selectedSet={selectedSet}
        onBackToQuiz={handleBackToQuiz}
        onTakeNewQuiz={handleTakeNewQuiz}
      />
    );
  }

  if (quizCompleted) {
    const scoreData = calculateScore();
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full mx-4">
          <div className="text-center">
            <div className="mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Quiz Completed!</h2>
              <p className="text-gray-600">Here are your results</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">{scoreData.correctCount}</div>
                  <div className="text-sm text-gray-600">Correct Answers</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-800">{scoreData.totalQuestions}</div>
                  <div className="text-sm text-gray-600">Total Sub-Questions</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{scoreData.totalQuestions > 0 ? ((scoreData.correctCount / scoreData.totalQuestions) * 100).toFixed(1) : 0}%</div>
                  <div className="text-sm text-gray-600">Score Percentage</div>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleResetQuiz}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
            >
              Take Another Quiz
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Set Selection Screen
  if (!selectedSet) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <h1 className="text-4xl font-bold text-gray-800">မေးခွန်းဟောင်းများ စုစည်းချက်</h1>
              {userHasPremium && (
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-semibold" title={getPremiumStatusInfo().message}>
                  ✨ Premium Member
                </div>
              )}
            </div>
            <p className="text-lg text-gray-600">
              စတင်ဖြေဆိုရန် မေးခွန်းဟောင်း တစ်စုံကို ရွေးချယ်လိုက်ပါ။
              {!userHasPremium && (
                <span className="block text-sm text-gray-500 mt-1">
                  💡 ပိုမို အဆင့်မြင့်သော Premium Sets များကို ဖြေဆိုနိုင်ရန် Premium Account သို့ မြှင့်တင်လိုက်ပါ။
                </span>
              )}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableSets.map((setName) => {
              const questionsCount = oldQuestions[setName].length;
              const setMetadata = getSetMetadata(setName);
              const isProtected = isSetProtected(setName);
              const canAccess = canUserAccessSet(setName);
              
              return (
                <div 
                  key={setName}
                  className={`bg-white rounded-lg shadow-lg hover:shadow-xl transition-all cursor-pointer border-2 ${
                    isProtected && !canAccess 
                      ? 'border-orange-200 hover:border-orange-300 bg-gradient-to-br from-white to-orange-50' 
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => handleSetSelection(setName)}
                >
                  <div className="p-6 relative">
                    {/* Lock icon for protected content */}
                    {isProtected && !canAccess && (
                      <div className="absolute top-4 right-4">
                        <div className="bg-orange-100 p-2 rounded-full">
                          <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-800 pr-8">
                        {setMetadata.title || setName}
                      </h3>
                      <div className={`text-sm font-medium px-3 py-1 rounded-full ${
                        isProtected && !canAccess 
                          ? 'bg-orange-100 text-orange-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {questionsCount} Questions
                      </div>
                    </div>
                    
                    <div className="text-gray-600 mb-4">
                      {setMetadata.description && typeof setMetadata.description === 'object' ? (
                        <div>
                          <p className="mb-2">{setMetadata.description.overview}</p>
                          <ul className="text-sm mt-2 space-y-1">
                            {setMetadata.description.topics.map((topic, index) => (
                              <li key={index}>• {topic}</li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <div>
                          <p>{setMetadata.description || 'Complete calculus problems covering various topics including:'}</p>
                          {!setMetadata.description && (
                            <ul className="text-sm mt-2 space-y-1">
                              <li>• Integration techniques</li>
                              <li>• Differential calculus</li>
                              <li>• Limits and continuity</li>
                              <li>• Applications of calculus</li>
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <span>⏱️ မိနစ် ၇၀ </span>
                      <span>{isProtected ? '💎 Premium' : '📝 Free Set'}</span>
                    </div>
                    
                    {isProtected && !canAccess ? (
                      <div className="space-y-2">
                        <button className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium py-3 px-4 rounded-lg transition-all transform hover:scale-105">
                          🔓 Premium သို့ မြှင့်တင်ရန်
                        </button>
                      </div>
                    ) : (
                      <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors">
                        စတင် ဖြေဆိုရန်
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mt-8 text-center">
            <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto">
              <h3 className="text-lg font-bold text-gray-800 mb-3">သတိပြုရန်: </h3>
              <div className="text-left text-gray-600 space-y-2">
                <p>• မေးခွန်းတစ်စုံကို ဖြေဆိုရန် စုစုပေါင်း မိနစ် ၇၀ အချိန်ရပါသည်။</p>
                <p>• mathematical expressions များကို ကောင်းမွန်စွာ ဖော်ပြနိုင်ရန် Math Input Help ကို သေချာစွာ လေ့လာလိုက်ပါ။</p>
                <p>• မေးခွန်းများကို အရှေ့၊ အနောက် button များဖြင့် Skip လုပ်နိုင်ပါသည်။ </p>
                <p>• submit လုပ်ပြီးပါက သင်၏အဖြေများကို အဖြေမှန်များနှင့် တိုက်ဆိုင်စစ်ဆေးမည်ဖြစ်ပြီး တစ်ပုဒ်ချင်းစီအလိုက် အသေးစိတ် တွက်နည်းများကို လေ့လာနိုင်မည်ဖြစ်ပါသည်။ </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // If no current question, show loading or error
  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4 text-center">
          <div className="text-gray-600">
            <p>Loading question...</p>
            <button
              onClick={handleResetQuiz}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Back to Set Selection
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header with timer and progress */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleResetQuiz}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 font-medium text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Back to Sets</span>
              </button>
              <div className="border-l border-gray-300 h-6"></div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Old Questions Quiz - {selectedSet}</h1>
                <p className="text-sm text-gray-600">
                  Question {currentQuestionIndex + 1} of {currentQuestions.length}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-lg font-mono font-bold text-red-600">
                  {formatTime(timeRemaining)}
                </div>
                <div className="text-xs text-gray-600">Time Remaining</div>
              </div>
              <button
                onClick={handleSubmitQuiz}
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Submit Quiz
              </button>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuestionIndex + 1) / currentQuestions.length) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          {/* Question */}
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                {currentQuestion.question_number}
              </span>
            </div>
            
            {/* Main question text */}
            <div className="text-lg text-gray-800 mb-4">
              <ExplanationRenderer content={getMainQuestionText(currentQuestion)} />
            </div>
            
            {/* Sub-questions if they exist */}
            {hasSubQuestions(currentQuestion) && (
              <div className="space-y-4">
                {getSubQuestions(currentQuestion).map((subQuestion, index) => {
                  const partKey = `part_${index + 1}`;
                  // Create ref for this sub-input if it doesn't exist
                  if (!subInputRefs[partKey]) {
                    subInputRefs[partKey] = React.createRef();
                  }
                  
                  return (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start space-x-3">
                        <span className="bg-blue-600 text-white text-sm font-medium px-2 py-1 rounded min-w-max">
                          {subQuestion.part_number}
                        </span>
                        <div className="flex-1">
                          <div className="text-gray-800 mb-3">
                            <ExplanationRenderer content={subQuestion.text} />
                          </div>
                          
                          {/* Sub-question answer input with visual math support */}
                          <div className="flex space-x-2">
                            <VisualMathInput
                              value={currentSubAnswers[partKey] || ''}
                              onChange={(e) => {
                                handleSubAnswerChange(partKey, e.target.value, e.target.selectionStart);
                              }}
                              onCursorChange={(e) => {
                                setActiveSubInput(partKey);
                                setCursorPosition(e.target.selectionStart);
                              }}
                              onFocus={() => handleSubInputFocus(partKey)}
                              placeholder={`Answer for ${subQuestion.part_number} (e.g., 23^e, pi)`}
                              className="flex-1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Answer input - only for simple questions */}
          {!hasSubQuestions(currentQuestion) && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Answer:
              </label>
              <div className="flex space-x-3 mb-3">
                <VisualMathInput
                  value={currentAnswer}
                  onChange={handleAnswerChange}
                  onCursorChange={handleCursorChange}
                  onFocus={handleMainInputFocus}
                  placeholder="Enter your mathematical answer (e.g., 23^e, pi, sqrt(x))"
                  className="flex-1"
                />
                <button
                  onClick={handleSaveAnswer}
                  disabled={!currentAnswer.trim()}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-lg transition-colors"
                >
                  Save Answer
                </button>
              </div>
              
              {/* Math Input Help */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Type patterns like 23^e or pi to see them rendered as mathematical notation.
                </div>
                <button
                  onClick={() => setShowMathHelp(true)}
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Math Input Help</span>
                </button>
              </div>
            </div>
          )}
          
          {/* Save button for sub-questions */}
          {hasSubQuestions(currentQuestion) && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <div className="text-sm text-gray-600">
                  Answer all parts above, then save your responses.
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowMathHelp(true)}
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Math Help</span>
                  </button>
                  <button
                    onClick={handleSaveAnswer}
                    disabled={Object.keys(currentSubAnswers).length === 0}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Save All Answers
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Navigation buttons */}
          <div className="flex justify-between items-center">
            <button
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Previous</span>
            </button>
            
            <div className="flex space-x-3">
              <button
                onClick={handleSkipQuestion}
                disabled={currentQuestionIndex === currentQuestions.length - 1}
                className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Skip
              </button>
              
              <button
                onClick={handleNextQuestion}
                disabled={currentQuestionIndex === currentQuestions.length - 1}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                <span>Next</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Math Input Help Modal */}
      <MathInputHelp 
        isOpen={showMathHelp} 
        onClose={() => setShowMathHelp(false)} 
      />
    </div>
  );
};

export default OldQuestionPage;